import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Close } from 'app/components/Common/Close'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { NewsletterFormSender } from 'app/utils/NewsletterFormSender'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { createContext, memo, useMemo, useState } from 'react'

import { Bottom } from './Bottom'
import { Contacts } from './Contacts'
import { Hotel } from './Hotel'
import { Newsletter } from './Newsletter'
import { Props as PolicyMenuItemProps } from './PolicyMenuItem'

export interface Props {
  address?: string
  cin?: string
  cir?: string
  companyName?: string
  email?: string
  facebookURL?: string
  googleMapsURL?: string
  hotels?: any
  instagramURL?: string
  languageCode: string
  languagePrefix?: string | null
  linkedinURL?: string
  phone?: string
  policyMenu?: { [key: string]: PolicyMenuItemProps[] }[]
  rea?: string
  registeredOffice?: string
  shareCapital?: string
  vat?: string
}

export const FooterContext = createContext({})

export const Footer = memo(function Footer({
  address,
  cin,
  cir,
  companyName,
  email,
  facebookURL,
  googleMapsURL,
  hotels,
  instagramURL,
  languageCode,
  languagePrefix,
  linkedinURL,
  phone,
  policyMenu,
  rea,
  registeredOffice,
  shareCapital,
  vat,
}: Props) {
  const sendFormTo = useMemo(
    () =>
      NewsletterFormSender({
        formURL: `${process.env.DIRECTUS_URL}/items/newsletter_submissions`,
      }),
    [],
  )

  const [_locked, setLocked] = useLockedBody()
  const [modalStatus, setModalStatus] = useState(false)

  return (
    <FooterContext.Provider
      value={{
        address,
        cin,
        cir,
        companyName,
        email,
        facebookURL,
        googleMapsURL,
        hotels,
        instagramURL,
        languageCode,
        languagePrefix,
        linkedinURL,
        phone,
        policyMenu,
        rea,
        registeredOffice,
        shareCapital,
        vat,
      }}
    >
      <Container row space="between" stretch tag="section" wrap>
        <LeftSide>
          <Contacts />

          <Bottom />
        </LeftSide>

        <RightSide>
          <Label>
            {useVocabularyData('footer-hotels-title', languageCode)}
          </Label>

          {hotels ? (
            <Hotels row wrap>
              {hotels
                .sort(
                  (a: any, b: any) =>
                    (a?.sort ? a.sort : 1) - (b?.sort ? b.sort : 1),
                )
                .map((item: any, index: any) => {
                  const translation = item.translations?.find(
                    (t: any) => t?.languages_code?.code === languageCode,
                  )

                  if (!translation) {
                    return null
                  }

                  return (
                    <Hotel
                      key={index}
                      image={item.image}
                      title={translation.title}
                      URL={translation.website_url}
                    />
                  )
                })}
            </Hotels>
          ) : null}

          <Label className="newsletter">
            {useVocabularyData('newsletter', languageCode)}
          </Label>

          <Description
            dangerouslySetInnerHTML={{
              __html: useVocabularyData('newsletter-text', languageCode),
            }}
          />

          <CTA
            label={useVocabularyData('subscribe', languageCode)}
            onClick={() => {
              if (!modalStatus) {
                setLocked(true)
                setModalStatus(true)
              } else {
                setLocked(false)
                setModalStatus(false)
              }
            }}
            variant="full-light"
          />

          <Modal
            className={modalStatus ? 'open' : undefined}
            dial={5}
            onClick={() => {
              setLocked(false)
              setModalStatus(false)
            }}
          >
            <Wrapper row stretch onClick={(e) => e.stopPropagation()}>
              <LinesLateral />
              <LinesBottom />

              <Close
                onClick={() => {
                  setLocked(false)
                  setModalStatus(false)
                }}
                variant="modal"
              />

              <Newsletter
                languageCode={languageCode}
                onNewsletterFormSendTo={async (data) => await sendFormTo(data)}
              />
            </Wrapper>
          </Modal>
        </RightSide>
      </Container>
    </FooterContext.Provider>
  )
})

const Container = styled(FlexBox)`
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  padding: 4.875rem 8.333vw 4.75rem;

  @media (max-width: 1199px) {
    align-items: center;
    padding: 3.75rem 1.875rem;
    text-align: center;
  }
`

const LeftSide = styled.div`
  width: 30%;

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const RightSide = styled.div`
  @media (max-width: 1199px) {
    margin-top: 3.75rem;
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  line-height: 1rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    &.newsletter {
      margin-top: 5.625rem;
    }
  }
`

const Hotels = styled(FlexBox)`
  margin: 1.875rem 0 3.75rem;

  @media (max-width: 1199px) {
    margin: 0 -1.25rem 0 0;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 0.5625rem;
`

const CTA = styled(Button)`
  margin-top: 1.5rem;
`

const Wrapper = styled(FlexBox)`
  width: 100%;
  max-width: 63.125rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight5};
  cursor: default;
  margin: auto;
  opacity: 0;
  transform: translateY(5.625rem);
  transition: 0.6s ease-in-out;
  &:before {
    content: '';
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight5};
    position: absolute;
    top: -1.875rem;
    right: 50%;
    left: 50%;
    transition: 0.3s ease-in-out;
  }

  @media (max-width: 767px) {
    display: block;
    height: 100vh;
    max-height: -webkit-fill-available;
    overflow-y: auto;
    transform: none;
    -webkit-overflow-scrolling: auto;
  }
`

const LinesLateral = styled.div`
  &:before,
  &:after {
    content: '';
    width: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight5};
    position: absolute;
    top: -1.875rem;
    bottom: calc(100% + 1.875rem);
    transition: 0.3s ease-in-out;
  }
  &:before {
    left: -1.875rem;
  }
  &:after {
    right: -1.875rem;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const LinesBottom = styled.div`
  &:before,
  &:after {
    content: '';
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight5};
    position: absolute;
    bottom: -1.875rem;
    transition: 0.3s ease-in-out;
  }
  &:before {
    left: -1.875rem;
    right: calc(100% + 1.875rem);
  }
  &:after {
    right: -1.875rem;
    left: calc(100% + 1.875rem);
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Modal = styled(FlexBox)`
  width: 100%;
  height: 100%;
  cursor: pointer;
  opacity: 0;
  padding: 0 1.5rem;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.15s ease-in-out;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.6;
    position: absolute;
    top: 0;
    left: 0;
  }
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 9999;

    ${Wrapper} {
      opacity: 1;
      transform: translateY(0);
      &:before {
        right: -1.875rem;
        left: -1.875rem;
        transition-delay: 0.3s;
      }
    }
    ${LinesLateral} {
      &:before,
      &:after {
        bottom: -1.875rem;
        transition-delay: 0.6s;
      }
    }
    ${LinesBottom} {
      &:before {
        right: 50%;
        transition-delay: 0.9s;
      }
      &:after {
        left: 50%;
        transition-delay: 0.9s;
      }
    }
  }
  &.first {
    animation: opacity 0.15s;
    > div {
      animation: fadeWrapper 0.6s;
    }
    img {
      animation: opacity 1s;
    }
  }

  @keyframes fadeWrapper {
    0% {
      transform: translateY(3.75rem);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes opacity {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @media (max-width: 767px) {
    padding: 0;
  }
`
